import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Heart, Shield, Users, ArrowRight } from 'lucide-react';
import { siteInfo, services, coreValues } from '../data/content';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1702059202331-15e11b354980" 
            alt="Peaceful resolution"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" style={{ color: '#5E7F8A' }}>
            Peaceful Family Resolution Starts Here
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto" style={{ color: '#5E7F8A' }}>
            Compassionate, professional mediation for California families seeking dignity and resolution without the courtroom battle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                style={{ backgroundColor: '#9BB2A2' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#8FA596'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9BB2A2'}
              >
                Book Free Consultation
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 border-2 transition-all"
                style={{ 
                  borderColor: '#5E7F8A',
                  color: '#5E7F8A'
                }}
              >
                Learn More
              </Button>
            </Link>
          </div>
        </div>

        {/* Sage divider line */}
        <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: '#9BB2A2' }}></div>
      </section>

      {/* Vision Statement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-2xl md:text-3xl font-semibold mb-4" style={{ color: '#5E7F8A' }}>
            Paix (pronounced "pay") — means "peace" in French.
          </p>
          <p className="text-xl md:text-2xl leading-relaxed italic" style={{ color: '#5E7F8A' }}>
            At Paix Mediation, peace is more than a word. It is the destination we work toward together — not the absence of conflict, but the presence of clarity, resolution, and the freedom to move forward. Every family deserves to find it.
          </p>
          <div className="w-24 h-1 mx-auto mt-8" style={{ backgroundColor: '#9BB2A2' }}></div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#5E7F8A' }}>
              Our Services
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: '#5E7F8A' }}>
              We help Los Angeles families navigate divorce, separation, and co‑parenting disputes through structured mediation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => {
              const icons = {
                heart: Heart,
                shield: Shield,
                users: Users
              };
              const Icon = icons[service.icon] || Heart;

              return (
                <Card key={service.id} className="border-0 shadow-md hover:shadow-lg transition-all">
                  <CardContent className="p-8 text-center">
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                      style={{ backgroundColor: '#9BB2A2' }}
                    >
                      <Icon size={28} className="text-white" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3" style={{ color: '#5E7F8A' }}>
                      {service.title}
                    </h3>
                    <p className="text-base" style={{ color: '#5E7F8A' }}>
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                variant="outline"
                className="border-2 transition-all"
                style={{ 
                  borderColor: '#5E7F8A',
                  color: '#5E7F8A'
                }}
              >
                View All Services
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-lg p-12 text-white text-center mb-12" style={{ backgroundColor: '#5E7F8A' }}>
            <h2 className="text-3xl font-bold mb-2">A Calm, Structured Approach to Resolution</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_calm-divorce-care/artifacts/515sxiey_replicate-prediction-p18ffaz8enrmt0cwb3kvhx97z8%20%281%29.png" 
                alt="Professional mediation session"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-6" style={{ color: '#5E7F8A' }}>
                Professional. Compassionate. Effective.
              </h3>
              <p className="text-lg mb-6 leading-relaxed" style={{ color: '#5E7F8A' }}>
                Paix Family Mediation is a Los Angeles–based family law and divorce mediation practice led by a seasoned business litigator trained in family and divorce mediation.
              </p>
              <p className="text-lg mb-6 leading-relaxed" style={{ color: '#5E7F8A' }}>
                We offer virtual and in-person mediation options, helping California-based couples resolve divorce and family disputes privately, efficiently, and respectfully.
              </p>
              <Link to="/about">
                <Button 
                  className="text-white"
                  style={{ backgroundColor: '#9BB2A2' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#8FA596'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9BB2A2'}
                >
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-12" style={{ color: '#5E7F8A' }}>
            Our Core Values
          </h2>
          <div className="space-y-4">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className="p-6 rounded-lg border-l-4 text-left transition-all hover:shadow-md"
                style={{ borderLeftColor: '#9BB2A2' }}
              >
                <p className="text-lg font-medium" style={{ color: '#5E7F8A' }}>
                  {value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#5E7F8A' }}>
            Ready to Move Forward Peacefully?
          </h2>
          <p className="text-xl mb-8" style={{ color: '#5E7F8A' }}>
            Schedule a free consultation to discuss your situation and explore how mediation can help.
          </p>
          <Link to="/contact">
            <Button 
              size="lg"
              className="text-white text-lg px-10 py-6 shadow-xl hover:shadow-2xl transition-all"
              style={{ backgroundColor: '#9BB2A2' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#8FA596'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9BB2A2'}
            >
              Schedule Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
